// src/contexts/SaleContext.js
import React, { createContext, useContext, useState, useEffect } from "react";
import { db } from "../firebase/firebase";
import { doc, onSnapshot, setDoc } from "firebase/firestore";
import { TYPE_OPTIONS, MEDICINE_TYPES, WEIGHT_TYPES, _updateCategoryBehaviorMap } from "../utils/productUtils";

// Helper: convert a raw Firebase list item (string or object) to a full object
function normaliseItem(item) {
  if (typeof item === "string") {
    const behavior = MEDICINE_TYPES.includes(item) ? "medicine"
      : WEIGHT_TYPES.includes(item) ? "weight"
      : "standard";
    return { name: item, behavior };
  }
  return { name: item.name, behavior: item.behavior || "standard" };
}

const SaleContext = createContext(null);

export const DEFAULT_SALE = {
  isActive: false,
  discountPercent: 0,
  label: "",
  startDate: "",
  endDate: "",
};

export function SaleProvider({ children }) {
  const [sale, setSale] = useState(DEFAULT_SALE);
  const [categoryDiscounts, setCategoryDiscounts] = useState({});
  // string[] — used by dropdowns / existing consumers
  const [categories, setCategories] = useState(TYPE_OPTIONS);
  // {name, behavior}[] — full objects for ManageCategories
  const [categoriesRaw, setCategoriesRaw] = useState(
    TYPE_OPTIONS.map(n => normaliseItem(n))
  );
  // name → behavior lookup for getProductBehaviorType
  const [categoryBehaviorMap, setCategoryBehaviorMap] = useState({});
  const [saleLoading, setSaleLoading] = useState(true);

  useEffect(() => {
    const saleRef = doc(db, "siteSettings", "saleInfo");
    const unsubscribeSale = onSnapshot(saleRef, (snap) => {
      if (snap.exists()) {
        const data = snap.data();
        
        // Ensure real-time date verification for global sale
        let effectiveIsActive = data.isActive;
        if (effectiveIsActive && data.startDate && data.endDate) {
          const now = new Date();
          now.setHours(0, 0, 0, 0); // start of today
          const start = new Date(data.startDate);
          start.setHours(0, 0, 0, 0);
          const end = new Date(data.endDate);
          end.setHours(23, 59, 59, 999); // end of that day
          
          if (now < start || now > end) {
            effectiveIsActive = false;
          }
        }
        
        setSale({ ...DEFAULT_SALE, ...data, effectiveIsActive });
      } else {
        setSale({ ...DEFAULT_SALE, effectiveIsActive: false });
      }
      setSaleLoading(false);
    });

    const catRef = doc(db, "siteSettings", "categoryDiscounts");
    const unsubscribeCat = onSnapshot(catRef, (snap) => {
      if (snap.exists()) {
        setCategoryDiscounts(snap.data());
      } else {
        setCategoryDiscounts({});
      }
    });

    // Dynamic category list — falls back to static TYPE_OPTIONS if not yet in Firestore
    const catNamesRef = doc(db, "siteSettings", "categories");
    const unsubscribeCatNames = onSnapshot(catNamesRef, (snap) => {
      if (snap.exists() && Array.isArray(snap.data()?.list) && snap.data().list.length > 0) {
        const raw = snap.data().list;
        const parsed = raw.map(normaliseItem);
        const names = parsed.map(item => item.name);
        const behaviorMap = {};
        parsed.forEach(item => { behaviorMap[item.name] = item.behavior; });

        setCategories(names);
        setCategoriesRaw(parsed);
        setCategoryBehaviorMap(behaviorMap);
        _updateCategoryBehaviorMap(behaviorMap); // push into productUtils module scope
      } else {
        const defaults = TYPE_OPTIONS.map(normaliseItem);
        const defaultMap = {};
        defaults.forEach(item => { defaultMap[item.name] = item.behavior; });

        setCategories(TYPE_OPTIONS);
        setCategoriesRaw(defaults);
        setCategoryBehaviorMap(defaultMap);
        _updateCategoryBehaviorMap(defaultMap);
      }
    });

    return () => {
      unsubscribeSale();
      unsubscribeCat();
      unsubscribeCatNames();
    };
  }, []);

  const saveSale = async (newSale) => {
    const ref = doc(db, "siteSettings", "saleInfo");
    await setDoc(ref, newSale, { merge: true });
  };

  const saveCategoryDiscounts = async (newCategoryDiscounts) => {
    const ref = doc(db, "siteSettings", "categoryDiscounts");
    await setDoc(ref, newCategoryDiscounts, { merge: false });
  };

  const saveCategories = async (newList) => {
    const ref = doc(db, "siteSettings", "categories");
    await setDoc(ref, { list: newList }, { merge: false });
  };

  // Compute effective discount label e.g. "15% OFF" for global sale
  const discountLabel = sale.effectiveIsActive && sale.discountPercent > 0
    ? `${sale.discountPercent}% OFF`
    : "";

  return (
    <SaleContext.Provider value={{
      sale,
      categoryDiscounts,
      categories,
      categoriesRaw,
      categoryBehaviorMap,
      saleLoading,
      saveSale,
      saveCategoryDiscounts,
      saveCategories,
      discountLabel
    }}>
      {children}
    </SaleContext.Provider>
  );
}

export function useSale() {
  return useContext(SaleContext);
}
